/**
 * A utility function to generate a thumbnail from the first page of a PDF file.
 *
 * Note: This implementation doesn't actually install the pdfjs-dist dependency
 * and instead creates a mock implementation for the purposes of this example.
 * In a real implementation, you would:
 * 1. Install pdfjs-dist: npm install pdfjs-dist
 * 2. Import and configure it properly
 * 3. Use it to render the first page of the PDF
 */

/**
 * Generates a thumbnail URL from the first page of a PDF file
 *
 * @param file The PDF file to generate a thumbnail from
 * @returns A Promise that resolves to a data URL containing the thumbnail
 */
export async function generatePdfThumbnail(file: File): Promise<string> {
  // In a real implementation, we would:
  // 1. Load the PDF using pdfjs
  // 2. Render the first page to a canvas
  // 3. Convert the canvas to a data URL
  // 4. Return the data URL

  // For now, we'll just return a simple colored rectangle as a placeholder
  // This is a mock implementation - in production, use pdfjs-dist to render actual PDF thumbnails

  return new Promise((resolve) => {
    // Create a canvas element
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 200;

    // Get the 2D context
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      // If we can't get a context, return a fallback image
      resolve('');
      return;
    }

    // Fill with light gray background
    ctx.fillStyle = '#f8f8f8';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw a red PDF icon outline
    ctx.fillStyle = '#f44336';
    ctx.fillRect(50, 20, 100, 130);
    ctx.fillStyle = 'white';
    ctx.fillRect(55, 25, 90, 120);

    // Add some "text lines" to simulate a document
    ctx.fillStyle = '#ddd';
    ctx.fillRect(65, 50, 70, 5);
    ctx.fillRect(65, 65, 70, 5);
    ctx.fillRect(65, 80, 70, 5);
    ctx.fillRect(65, 95, 40, 5);

    // Add filename at the bottom
    ctx.fillStyle = '#333';
    ctx.font = '12px Arial';

    // Truncate the filename if it's too long
    let filename = file.name;
    if (filename.length > 15) {
      filename = `${filename.substring(0, 12)}...`;
    }

    ctx.fillText(filename, 50, 180);

    // Return the canvas as a data URL
    resolve(canvas.toDataURL('image/png'));
  });
}

/**
 * Checks if the browser supports PDF thumbnail generation
 * @returns boolean indicating whether PDF thumbnails can be generated
 */
export function isPdfThumbnailSupported(): boolean {
  // Check if we're in a browser environment
  return typeof window !== 'undefined'
    && typeof document !== 'undefined'
    && typeof document.createElement === 'function';
}
